import { type Component, For } from "solid-js";
import { Skeleton } from "@fleet/ui";

/**
 * Granular sub-skeletons extracted from {@link WorkspaceSkeleton} so individual
 * data-bound regions of the app shell can show their own placeholder while their
 * query loads (or PowerSync is still initializing), without holding back the
 * static chrome around them.
 */

/** Small inline placeholder for a trailing count badge. */
export const CountBadgeSkeleton: Component = () => <Skeleton class="h-3 w-5 rounded bg-muted/15" />;

/** Main content placeholder (dashboard cards + content rows). */
export const MainContentSkeleton: Component = () => (
  <div class="flex-1 overflow-hidden p-6 space-y-6 select-none pointer-events-none">
    {/* Dashboard cards placeholder */}
    <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
      <For each={Array.from({ length: 3 })}>
        {() => (
          <div class="p-5 border border-border/30 bg-card/5 rounded-xl space-y-3">
            <div class="flex items-center justify-between">
              <Skeleton class="h-4 w-24 bg-muted/30 rounded" />
              <Skeleton class="h-3 w-10 bg-muted/20 rounded" />
            </div>
            <div class="space-y-1.5 pt-1">
              <Skeleton class="h-2.5 w-full bg-muted/15 rounded" />
              <Skeleton class="h-2.5 w-[75%] bg-muted/15 rounded" />
            </div>
          </div>
        )}
      </For>
    </div>

    {/* Table / List layout placeholder */}
    <div class="space-y-4 pt-2">
      <div class="flex items-center justify-between">
        <Skeleton class="h-5 w-24 bg-muted/35 rounded" />
        <Skeleton class="h-7 w-16 bg-muted/20 rounded" />
      </div>

      {/* Content list rows skeleton */}
      <div class="border border-border/30 bg-card/5 rounded-xl divide-y divide-border/20">
        <For each={Array.from({ length: 4 })}>
          {() => (
            <div class="flex items-center gap-4 p-4">
              <Skeleton class="h-4.5 w-4.5 rounded-full bg-muted/30 shrink-0" />
              <div class="flex-1 space-y-1.5">
                <Skeleton class="h-3.5 w-1/3 bg-muted/25 rounded" />
                <Skeleton class="h-2.5 w-1/5 bg-muted/15 rounded" />
              </div>
              <Skeleton class="h-3 w-12 bg-muted/20 rounded" />
            </div>
          )}
        </For>
      </div>
    </div>
  </div>
);
